/**
 * UI-3: Frontend Replay Engine
 *
 * Derives entity state from event history by replaying events chronologically.
 * This mirrors the backend OPS-21E replay concept but runs entirely in the browser.
 *
 * NO backend calls — pure function over TimelineEvent[].
 * NO mutation of input — returns fresh derived state.
 */

import type {
  TimelineEvent,
  RoomState,
  GuestState,
  RequestState,
  StateTransition,
  RoomOccupancy,
  HousekeepingStatus,
  RequestStatus,
} from './entityEventTypes';

// ─── Room Replay ────────────────────────────────────────────────────────────

export function replayRoomState(events: readonly TimelineEvent[]): RoomState {
  let occupancy: RoomOccupancy = 'VACANT';
  let housekeepingStatus: HousekeepingStatus = 'CLEAN';
  let currentGuestName: string | null = null;
  let currentStayId: string | null = null;
  let checkInDate: string | null = null;
  let checkOutDate: string | null = null;
  let hasMaintenanceIssue = false;
  let activeRequestCount = 0;
  let lastEventTimestamp: string | null = null;
  let roomNumber = '';

  for (const event of events) {
    lastEventTimestamp = event.timestamp;
    if (event.roomNumber) roomNumber = event.roomNumber;

    switch (event.eventType) {
      case 'STAY_RESERVED':
        if (occupancy === 'VACANT') occupancy = 'RESERVED';
        currentStayId = event.stayId || currentStayId;
        currentGuestName = event.guestName ?? currentGuestName;
        break;
      case 'STAY_CHECKED_IN':
        occupancy = 'OCCUPIED';
        currentStayId = event.stayId || currentStayId;
        currentGuestName = event.guestName ?? currentGuestName;
        checkInDate = event.timestamp;
        break;
      case 'STAY_CHECKED_OUT':
        occupancy = 'VACANT';
        checkOutDate = event.timestamp;
        housekeepingStatus = 'DIRTY';
        break;
      case 'STAY_CANCELLED':
        if (occupancy === 'RESERVED') occupancy = 'VACANT';
        break;
      case 'HK_TASK_CREATED':
        housekeepingStatus = 'DIRTY';
        break;
      case 'HK_TASK_STARTED':
        housekeepingStatus = 'CLEANING';
        break;
      case 'HK_TASK_COMPLETED':
        housekeepingStatus = 'CLEAN';
        break;
      case 'HK_TASK_BLOCKED':
        housekeepingStatus = 'OUT_OF_SERVICE';
        break;
      case 'MAINT_REPORTED':
        hasMaintenanceIssue = true;
        activeRequestCount++;
        break;
      case 'MAINT_RESOLVED':
        hasMaintenanceIssue = false;
        activeRequestCount = Math.max(0, activeRequestCount - 1);
        break;
      case 'REQUEST_CREATED':
        activeRequestCount++;
        break;
      case 'REQUEST_COMPLETED':
      case 'REQUEST_CANCELLED':
        activeRequestCount = Math.max(0, activeRequestCount - 1);
        break;
    }
  }

  return {
    roomNumber,
    occupancy,
    housekeepingStatus,
    currentGuestName,
    currentStayId,
    checkInDate,
    checkOutDate,
    hasMaintenanceIssue,
    activeRequestCount,
    lastEventTimestamp,
  };
}

// ─── Guest Replay ───────────────────────────────────────────────────────────

export function replayGuestState(guestId: string, events: readonly TimelineEvent[]): GuestState {
  let guestName = guestId;
  let roomNumber: string | null = null;
  let stayStatus: GuestState['stayStatus'] = 'PRE_ARRIVAL';
  let checkInDate: string | null = null;
  let checkOutDate: string | null = null;
  let totalRequests = 0;
  let openRequests = 0;
  let totalOrders = 0;
  let ecoOptedIn = false;
  let lastInteraction: string | null = null;

  for (const event of events) {
    lastInteraction = event.timestamp;
    if (event.guestName) guestName = event.guestName;
    if (event.roomNumber) roomNumber = event.roomNumber;

    switch (event.eventType) {
      case 'STAY_RESERVED':
        stayStatus = 'PRE_ARRIVAL';
        break;
      case 'STAY_CHECKED_IN':
        stayStatus = 'CHECKED_IN';
        checkInDate = event.timestamp;
        break;
      case 'STAY_CHECKED_OUT':
        stayStatus = 'CHECKED_OUT';
        checkOutDate = event.timestamp;
        break;
      case 'STAY_CANCELLED':
        stayStatus = 'CANCELLED';
        break;
      case 'REQUEST_CREATED':
        totalRequests++;
        openRequests++;
        break;
      case 'REQUEST_COMPLETED':
      case 'REQUEST_CANCELLED':
        openRequests = Math.max(0, openRequests - 1);
        break;
      case 'ORDER_PLACED':
        totalOrders++;
        break;
      case 'ECO_OPT_IN':
        ecoOptedIn = true;
        break;
      case 'ECO_OPT_OUT':
        ecoOptedIn = false;
        break;
    }
  }

  return {
    guestId,
    guestName,
    roomNumber,
    stayStatus,
    checkInDate,
    checkOutDate,
    totalRequests,
    openRequests,
    totalOrders,
    ecoOptedIn,
    lastInteraction,
  };
}

// ─── Request Replay ─────────────────────────────────────────────────────────

export function replayRequestState(requestId: string, events: readonly TimelineEvent[]): RequestState {
  let status: RequestStatus = 'NEW';
  let roomNumber: string | null = null;
  let guestName: string | null = null;
  let category: string | null = null;
  let description: string | null = null;
  let assignedTo: string | null = null;
  let createdAt: string | null = null;
  let resolvedAt: string | null = null;
  let slaDeadline: string | null = null;
  let isOverdue = false;
  let escalationCount = 0;

  for (const event of events) {
    if (event.roomNumber) roomNumber = event.roomNumber;
    if (event.guestName) guestName = event.guestName;

    switch (event.eventType) {
      case 'REQUEST_CREATED':
      case 'MAINT_REPORTED':
        // Only set category/description from creation events — assignment/status
        // events carry stale or placeholder values that must not overwrite.
        if (event.category) category = event.category;
        if (event.metadata?.description) description = String(event.metadata.description);
        status = 'NEW';
        createdAt = event.timestamp;
        if (event.metadata?.slaDeadline) {
          slaDeadline = String(event.metadata.slaDeadline);
        }
        break;
      case 'REQUEST_ASSIGNED':
      case 'MAINT_ASSIGNED':
        status = 'ASSIGNED';
        // Prefer metadata.assignee (staff name) over actor — actor is the typed
        // literal 'staff' for PILOT-26 assignments, not a human-readable name.
        assignedTo = (event.metadata?.assignee ? String(event.metadata.assignee) : null)
          ?? event.actor
          ?? null;
        break;
      case 'REQUEST_IN_PROGRESS':
        status = 'IN_PROGRESS';
        break;
      case 'REQUEST_ESCALATED':
        status = 'ESCALATED';
        escalationCount++;
        break;
      case 'REQUEST_COMPLETED':
      case 'MAINT_RESOLVED':
        status = 'COMPLETED';
        resolvedAt = event.timestamp;
        break;
      case 'REQUEST_CANCELLED':
        status = 'CANCELLED';
        resolvedAt = event.timestamp;
        break;
    }

    // Check SLA
    if (slaDeadline && status !== 'COMPLETED' && status !== 'CANCELLED') {
      isOverdue = new Date(event.timestamp).getTime() > new Date(slaDeadline).getTime();
    }
  }

  return {
    requestId,
    status,
    roomNumber,
    guestName,
    category,
    description,
    assignedTo,
    createdAt,
    resolvedAt,
    slaDeadline,
    isOverdue,
    escalationCount,
  };
}

// ─── State Transitions ──────────────────────────────────────────────────────

/**
 * Extract state transitions from event history.
 * Identifies field changes across events for the State Evolution Panel.
 */
export function deriveStateTransitions(
  entityType: 'room' | 'guest' | 'request',
  events: readonly TimelineEvent[],
): StateTransition[] {
  const transitions: StateTransition[] = [];

  if (entityType === 'room') {
    let prevOccupancy: string = 'VACANT';
    let prevHkStatus: string = 'CLEAN';

    for (const event of events) {
      const state = replayRoomState(events.slice(0, events.indexOf(event) + 1));

      if (state.occupancy !== prevOccupancy) {
        transitions.push({
          timestamp: event.timestamp,
          field: 'Occupancy',
          from: prevOccupancy,
          to: state.occupancy,
          triggeredBy: event,
        });
        prevOccupancy = state.occupancy;
      }

      if (state.housekeepingStatus !== prevHkStatus) {
        transitions.push({
          timestamp: event.timestamp,
          field: 'Housekeeping',
          from: prevHkStatus,
          to: state.housekeepingStatus,
          triggeredBy: event,
        });
        prevHkStatus = state.housekeepingStatus;
      }
    }
  } else if (entityType === 'guest') {
    let prevStatus: string = 'PRE_ARRIVAL';

    for (const event of events) {
      const guestId = events[0]?.stayId ?? '';
      const state = replayGuestState(guestId, events.slice(0, events.indexOf(event) + 1));

      if (state.stayStatus !== prevStatus) {
        transitions.push({
          timestamp: event.timestamp,
          field: 'Stay Status',
          from: prevStatus,
          to: state.stayStatus,
          triggeredBy: event,
        });
        prevStatus = state.stayStatus;
      }
    }
  } else if (entityType === 'request') {
    let prevStatus: string = 'NEW';

    for (const event of events) {
      const requestId = events[0]?.entityId ?? '';
      const state = replayRequestState(requestId ?? '', events.slice(0, events.indexOf(event) + 1));

      if (state.status !== prevStatus) {
        transitions.push({
          timestamp: event.timestamp,
          field: 'Status',
          from: prevStatus,
          to: state.status,
          triggeredBy: event,
        });
        prevStatus = state.status;
      }
    }
  }

  return transitions;
}
